

import { Button } from '@/components/ui/button';
import { MoreHorizontal, Edit, Share2, Trash2, FileEdit, FileCheck } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
interface GamePlanCardProps {
  id: string;
  name: string;
  description?: string;
  isTemplate?: boolean;
  isAdmin?: boolean;
  onEdit: (id: string) => void;
  onShare: (id: string) => void;
  onDelete: (id: string) => void;
  onChangeDetails: (id: string) => void;
  onConvertToTemplate: (id: string) => void;
}
export function GamePlanCard({
  id,
  name,
  description,
  isTemplate = false,
  isAdmin = false,
  onEdit,
  onShare,
  onDelete,
  onChangeDetails,
  onConvertToTemplate
}: GamePlanCardProps) {
  // Determine the card's appearance based on its type
  const cardClass = cn("relative flex flex-col p-4 border rounded-lg shadow-sm transition-all hover:shadow-md", {
    "bg-green-50 border-green-200": isTemplate && !isAdmin,
    "bg-purple-50 border-purple-200": isAdmin,
    "bg-blue-50 border-blue-200": !isTemplate && !isAdmin
  });

  // Get badge text based on plan type
  const getBadgeText = () => {
    if (isAdmin) return "Admin Template";
    if (isTemplate) return "Template";
    return "Game Plan";
  };

  // Get badge color based on plan type
  const badgeClass = cn("absolute top-2 right-10 px-2 py-1 text-xs font-bold rounded-full", {
    "bg-green-200 text-green-800": isTemplate && !isAdmin,
    "bg-purple-200 text-purple-800": isAdmin,
    "bg-blue-200 text-blue-800": !isTemplate && !isAdmin
  });
  
  return <div className={cardClass}>
      <div className={badgeClass}>{getBadgeText()}</div>
      
      <div className="flex justify-between items-start">
        {/* Increased right padding to ensure title doesn't overlap with badge or menu */}
        <h3 className="text-lg font-semibold line-clamp-2 pr-36 mb-2 text-left">{name}</h3>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 absolute top-2 right-2">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(id)}>
              <Edit className="mr-2 h-4 w-4" />
              <span>Edit</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onShare(id)}>
              <Share2 className="mr-2 h-4 w-4" />
              <span>Share</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onChangeDetails(id)}>
              <FileEdit className="mr-2 h-4 w-4" />
              <span>Edit Details</span>
            </DropdownMenuItem>
            
            {/* Only show Convert to Template option for regular plans */}
            {!isTemplate && !isAdmin && <DropdownMenuItem onClick={() => onConvertToTemplate(id)}>
                <FileCheck className="mr-2 h-4 w-4" />
                <span>Convert to Template</span>
              </DropdownMenuItem>}
            
            {/* Only show Delete option if not an admin template */}
            {!isAdmin && <DropdownMenuItem onClick={() => onDelete(id)} className="text-red-500 focus:text-red-500">
              <Trash2 className="mr-2 h-4 w-4" />
              <span>Delete</span>
            </DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {description && <p className="mt-2 text-sm text-gray-600 line-clamp-3">
          {description}
        </p>}
      
      <div className="mt-auto pt-4 flex gap-2">
        <Button onClick={() => onEdit(id)} className={cn("flex-1", {
        "bg-green-600 hover:bg-green-700": isTemplate && !isAdmin,
        "bg-purple-600 hover:bg-purple-700": isAdmin,
        "bg-blue-600 hover:bg-blue-700": !isTemplate && !isAdmin
      })}>
          {isTemplate || isAdmin ? "Use Template" : "Open"}
        </Button>
        <Button onClick={() => onShare(id)} variant="outline" className={cn("flex-none", {
        "border-green-600 text-green-600 hover:bg-green-50": isTemplate && !isAdmin,
        "border-purple-600 text-purple-600 hover:bg-purple-50": isAdmin,
        "border-blue-600 text-blue-600 hover:bg-blue-50": !isTemplate && !isAdmin
      })}>
          <Share2 className="h-4 w-4" />
        </Button>
      </div>
    </div>;
}
